import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";
import { Menu, X } from "lucide-react";
import { BrandMark } from "../BrandMark";
import { ThemeToggle } from "../ThemeToggle";
import { trackLandingCta } from "../../hooks/useLandingAnalytics";
import { scrollToSection, scrollToTop } from "./scrollToSection";

const ANCHORS = [
  { hash: "#capabilities", key: "features" },
  { hash: "#product-tour", key: "tour" },
  { hash: "#verticals", key: "verticals" },
  { hash: "#pricing", key: "earlyAccess" },
  { hash: "#faq", key: "faq" },
] as const;

function LangSwitch({ className = "" }: { className?: string }) {
  const lang = (i18n.language ?? "en").split("-")[0];
  return (
    <div
      className={`flex items-center rounded-full border border-slate-200 bg-slate-100 p-0.5 dark:border-white/10 dark:bg-slate-800/60 ${className}`}
    >
      {(["en", "es"] as const).map((code) => (
        <button
          key={code}
          onClick={() => i18n.changeLanguage(code)}
          className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold transition-all ${
            lang === code
              ? "bg-blue-600 text-white"
              : "text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
          }`}
        >
          {code.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

export function LandingNavbar() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const linkCls =
    "text-sm text-slate-600 hover:text-slate-900 transition-colors dark:text-slate-300 dark:hover:text-white";

  // border-b stays mounted and only its COLOR transitions — toggling the
  // border class itself (with transition-all) flashed a thin light line while
  // fast-scrolling back to the top.
  return (
    <header
      className={`fixed top-0 left-0 right-0 z-40 border-b transition duration-300 ${
        scrolled
          ? "border-slate-200/80 bg-white/95 shadow-sm md:bg-white/80 md:backdrop-blur-lg dark:border-white/5 dark:bg-slate-950/95 md:dark:bg-slate-950/80 dark:shadow-xl"
          : "border-transparent bg-transparent shadow-none"
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className={`flex items-center justify-between transition-all ${scrolled ? "h-14" : "h-16"}`}>
          <Link
            to="/"
            onClick={(e) => {
              // Already on the landing — the logo means "back to the top".
              e.preventDefault();
              scrollToTop();
            }}
            className="flex items-center gap-2 flex-shrink-0"
          >
            <BrandMark />
            <span className="text-lg font-bold tracking-tight text-slate-900 dark:text-white">
              Adam
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {ANCHORS.map(({ hash, key }) => (
              <a
                key={key}
                href={hash}
                onClick={(e) => {
                  // Scroll without writing the hash into the URL — a hash
                  // makes a later refresh re-anchor mid-page.
                  e.preventDefault();
                  scrollToSection(hash);
                }}
                className={linkCls}
              >
                {t(`landing.nav.${key}`)}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2.5">
            <ThemeToggle />
            <LangSwitch className="hidden sm:flex" />
            <Link to="/login" className={`hidden sm:inline ${linkCls}`}>
              {t("landing.nav.login")}
            </Link>
            <Link
              to="/register?src=landing"
              onClick={() => trackLandingCta("navbar_register", "navbar")}
              className="relative rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 hover:from-blue-400 hover:to-indigo-500 text-white font-semibold px-4 py-1.5 text-sm shadow-lg shadow-blue-500/30 transition-all"
            >
              {t("landing.nav.getStarted")}
            </Link>
            <button
              className="lg:hidden p-1 text-slate-700 dark:text-slate-200"
              aria-label="Menu"
              onClick={() => setOpen(!open)}
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white/95 animate-slide-in dark:border-white/5 dark:bg-slate-950/95">
          <nav className="px-4 py-4 space-y-2 text-sm text-slate-700 dark:text-slate-200">
            {ANCHORS.map(({ hash, key }) => (
              <a
                key={key}
                href={hash}
                onClick={(e) => {
                  e.preventDefault();
                  setOpen(false);
                  scrollToSection(hash);
                }}
                className="block py-2"
              >
                {t(`landing.nav.${key}`)}
              </a>
            ))}
            <Link to="/login" className="block py-2">
              {t("landing.nav.login")}
            </Link>
            <div className="pt-2">
              <LangSwitch className="inline-flex" />
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
